'use client';

import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useSite } from 'src/context/providers/SiteProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useGetEntityPlaysQuery } from 'src/api/redux/profileConfiguration';
import type {
  MatchedPlay,
  MatchedPlayWidget,
  PlayItemWidget,
} from 'src/api/redux/profileConfiguration/types';
import {
  WidgetRenderer,
  toProfileWidget,
} from 'src/components/Profiles/shared/widgetRenderer';
import { EvidenceInfoIcon, PlayEvidenceList } from 'src/components/Profiles/shared/widgets/PlaysSection/components';
import { sortPlayWidgets } from 'src/components/Profiles/shared/widgets/PlaysSection/components/PlaysCard';
import { TalkTrack } from 'src/components/Profiles/shared/widgets/TalkTrack';
import { ProviderClassifications } from 'src/components/Profiles/shared/widgets';
// Side-effect import: registers the widget resolver so WidgetRenderer
// can resolve widget components.
import 'src/components/Profiles/shared/profileWidgetConfig';
import { EmptyState } from 'src/components/PulseUI';
import { InsightCard, BackButton, OverviewButton } from './components';

type SlideState = { view: 'list' } | { view: 'detail'; playIndex: number };

const PlaysSkeleton = () => (
  <div className="flex flex-col gap-4" data-testid="profile-drawer-plays-loading">
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className="bg-white border border-neutral-200 rounded-xl px-5 py-4 flex items-center justify-between gap-3 animate-pulse"
      >
        <div className="flex flex-col gap-2 flex-1">
          <div className="h-4 w-1/3 bg-neutral-200 rounded" />
          <div className="h-3 w-2/3 bg-neutral-100 rounded" />
        </div>
        <div className="h-3 w-3 bg-neutral-200 rounded" />
      </div>
    ))}
  </div>
);

const ProfileDrawerConfigV2 = () => {
  const { state: siteState } = useSite();
  const providerId = siteState?.profile?.provider?.id;
  const { entityDetails } = useProfile();

  const { data: playsData, isLoading } = useGetEntityPlaysQuery(
    { entityId: providerId },
    { skip: !providerId }
  );

  const matchedPlays = playsData?.results ?? [];

  const [slideState, setSlideState] = useState<SlideState>({ view: 'list' });
  const detailPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSlideState({ view: 'list' });
  }, [providerId]);

  const handleSelectPlay = (index: number) => {
    setSlideState({ view: 'detail', playIndex: index });
    requestAnimationFrame(() => {
      if (detailPanelRef.current) {
        detailPanelRef.current.scrollTop = 0;
      }
    });
  };

  const handleBack = () => {
    setSlideState({ view: 'list' });
  };

  const isDetail = slideState.view === 'detail';
  const selectedPlay = isDetail ? matchedPlays[slideState.playIndex] : null;

  const renderDetailContent = () => {
    if (!selectedPlay) return null;

    return (
      <>
        <TalkTrack
          talkTrack={selectedPlay.talk_track}
          playId={selectedPlay.id}
          label={selectedPlay.label}
        />
        <PlayEvidenceList
          evidence={selectedPlay.trigger_evidence?.children ?? []}
          signals={selectedPlay.triggers?.children}
          triggerLogic={selectedPlay.trigger_logic}
          playEvidence={selectedPlay.evidence}
        />
        <div className="@container">
          <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-4">
            {sortPlayWidgets(selectedPlay.widgets)
              .map((pw: MatchedPlayWidget, i: number) => (
                <div
                  key={`${pw.code}-${pw.content_key ?? i}`}
                  data-testid={`widget-card-${pw.code}-${pw.content_key ?? i}`}
                >
                  <WidgetRenderer
                    widget={toProfileWidget(pw as unknown as PlayItemWidget)}
                    variant="default"
                    parentLabel={selectedPlay.label}
                  />
                </div>
              ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      className="flex-1 min-h-0 overflow-hidden"
      data-testid="profile-drawer-config-v2"
      aria-label="Profile configuration"
    >
      <div
        className={`flex w-[200%] h-full transition-transform duration-300 ease-in-out ${
          isDetail ? '-translate-x-1/2' : 'translate-x-0'
        }`}
        data-testid="sliding-track"
      >
        <div
          className="w-1/2 h-full overflow-y-auto p-4"
          data-testid="list-panel"
        >
          {isLoading ? (
            <PlaysSkeleton />
          ) : (
            <div className="flex flex-col gap-4">
              {matchedPlays.length === 0 ? (
                <EmptyState
                  title="No active plays"
                  description="This provider doesn't match any active plays"
                  className="flex-1 justify-center"
                />
              ) : (
                matchedPlays.map((play: MatchedPlay, index: number) => (
                  <InsightCard
                    key={play.id}
                    label={play.label}
                    onClick={() => handleSelectPlay(index)}
                    data-testid={`play-section-${play.id}`}
                    headerAction={
                      play.evidence ? (
                        <EvidenceInfoIcon
                          evidence={play.evidence}
                          data-testid={`play-evidence-icon-${play.id}`}
                        />
                      ) : undefined
                    }
                  />
                ))
              )}
              {/* <ProviderClassifications /> */}
              <OverviewButton />
            </div>
          )}
        </div>

        <div
          ref={detailPanelRef}
          className="w-1/2 h-full overflow-y-auto p-4"
          data-testid="detail-panel"
        >
          {isDetail && selectedPlay && (
            <div className="flex flex-col gap-4">
              <BackButton onClick={handleBack} label="Back" />
              {renderDetailContent()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileDrawerConfigV2;
